import { orgAPI } from "@/api/api";
import { useCreateCanvas } from "@/api/canvas";
import { ROUTES } from "@/constants/routes";
import { route } from "@/utils/route";
import { useCallback, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useOnboardingStage } from "./gate";
import { useOnboardingRecord, useOnboardingStatus } from "./status";
import { openTourPatch, readProgress } from "./tour/progress";
import {
  DEFAULT_TOUR,
  TOUR_CANVAS_DESCRIPTION,
  TOUR_CANVAS_NAME,
} from "./tours";
import type { Handoff } from "./types";

/** An archived or deleted canvas is no canvas: the tour starts a new one. */
async function canOpen(canvasId: string): Promise<boolean> {
  try {
    const canvas = await orgAPI.canvas.getById(canvasId);
    return !canvas.archived_at;
  } catch {
    return false;
  }
}

/**
 * Open the walkthrough. The record moves to `in_progress` in the same patch
 * that names the tour's canvas, once that canvas exists: writing the status
 * on the click would unmount the welcome dialog mid-create, leaving the user
 * on a home that offers to finish a tour with no canvas behind it. It
 * remembers the canvas, so a second start resumes that one instead of leaving
 * another behind.
 */
export function useStartTour(): { start: () => void; pending: boolean } {
  const navigate = useNavigate();
  const status = useOnboardingStatus();
  const { record } = useOnboardingRecord();
  const [resolving, setResolving] = useState(false);

  const open = useCallback(
    (canvasId: string, handoff?: Handoff) => {
      navigate(route(ROUTES.CANVAS.DETAIL, { canvasId }), {
        state: handoff ? { onboarding: handoff } : undefined,
      });
    },
    [navigate],
  );

  const createCanvas = useCreateCanvas({
    onSuccess: (canvas) => {
      // One write, before the navigate: the patch shows in the cache at once,
      // so the canvas page reads a record that is started and already names
      // the canvas it is opening.
      void status.patch({
        ...openTourPatch(record, DEFAULT_TOUR, canvas.id),
        global: "in_progress",
      });
      open(canvas.id, { tour: DEFAULT_TOUR });
    },
    onError: () => {
      toast.error("Couldn't open a canvas. Please try again.");
    },
  });

  const start = useCallback(() => {
    if (createCanvas.isPending || resolving) return;

    const existing = readProgress(record, DEFAULT_TOUR);
    if (!existing || existing.done) {
      createCanvas.mutate({
        name: TOUR_CANVAS_NAME,
        description: TOUR_CANVAS_DESCRIPTION,
      });
      return;
    }

    setResolving(true);
    void canOpen(existing.canvasId).then((openable) => {
      setResolving(false);
      if (!openable) {
        createCanvas.mutate({
          name: TOUR_CANVAS_NAME,
          description: TOUR_CANVAS_DESCRIPTION,
        });
        return;
      }
      // Nothing to write: the record is already started and already names
      // this canvas, so the slot picks the tour up where it left off without
      // a handoff.
      open(existing.canvasId);
    });
  }, [createCanvas, open, record, resolving]);

  return { start, pending: createCanvas.isPending || resolving };
}

export interface OnboardingEntry {
  /** The user started or skipped the walkthrough and has not finished it. */
  visible: boolean;
  /** The walkthrough is still ahead of the user: not started, started or skipped. */
  offered: boolean;
  /** The canvas the walkthrough runs on, once one exists and it is not done. */
  canvasId?: string;
  start: () => void;
  dismiss: () => void;
  pending: boolean;
}

/**
 * The "Finish onboarding" offer on the home browser. The card is production's;
 * whether it applies, and what its two actions do, are onboarding's.
 */
export function useOnboardingEntry(): OnboardingEntry {
  const stage = useOnboardingStage();
  const status = useOnboardingStatus();
  const { record } = useOnboardingRecord();
  const { start, pending } = useStartTour();
  const progress = readProgress(record, DEFAULT_TOUR);
  const dismiss = useCallback(() => {
    void status.finish();
  }, [status]);
  const visible = stage === "started" || stage === "skipped";
  return {
    visible,
    offered: visible || stage === "new",
    canvasId: progress && !progress.done ? progress.canvasId : undefined,
    start,
    dismiss,
    pending,
  };
}
